import { normalize } from "normalizr";
import { from, of, merge } from "rxjs";
import { filter, bufferTime, groupBy, toArray } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { BaseEpicsBuilder } from "../epicsBuilder.js";
import "../actions.js";
import { fetchBufferTimeSpan } from "./types/base.js";
import { getEntityActions } from "./actions.js";
import { EntityKeysMapper } from "./schemas.js";
import { defineMessages } from "react-intl";
import "@opentelemetry/api";
import "../../otel/TraceProvider.js";
import "react";
import "react-error-boundary";
import { mergeMap, map } from "../../otel/rxjs.js";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import { messageActions } from "../message/actions.js";
import { getViewActions } from "../view/actions.js";
const localized = defineMessages({
  duplicateFilterMessage: {
    id: "EntityEpicsBuilder_duplicateFilterMessage",
    defaultMessage: "Failed to fetch {type} due conflict in filter {filter}"
  },
  entityFetchFailed: {
    id: "EntityEpicsBuilder_entityFetchFailed",
    defaultMessage: "Failed to fetch {type}"
  }
});
class EntityEpicsBuilder extends BaseEpicsBuilder {
  _serviceFactory;
  _entityActions;
  _entityTypes;
  _schemaMapper;
  _viewActions;
  constructor(serviceFactory, entityTypes, schemaMapper) {
    super();
    this._serviceFactory = serviceFactory;
    this._entityActions = getEntityActions();
    this._entityTypes = entityTypes;
    this._schemaMapper = schemaMapper;
    this._viewActions = getViewActions();
  }
  buildEpicList() {
    return [
      this.buildFetchEpic(),
      ...this.buildFetchRequestEpics(),
      this.buildFetchRequestSuccessEpic(),
      this.buildFetchRequestFailureEpic(),
      this.buildCollectGarbageEpic()
    ];
  }
  buildFetchEpic() {
    return (action$) => action$.pipe(
      filter(isActionOf(this._entityActions.fetch)),
      bufferTime(fetchBufferTimeSpan),
      mergeMap(
        (actions) => from(actions).pipe(
          map((action) => action.payload),
          groupBy((p) => p.type),
          mergeMap(
            (entityGroup$) => entityGroup$.pipe(
              groupBy((p) => p.filter),
              mergeMap(
                (filterGroup$) => filterGroup$.pipe(toArray())
              )
            )
          ),
          mergeMap((payloadGroup) => {
            let result = of();
            if (payloadGroup.length) {
              const joinedFields = payloadGroup.map((p) => p.fields).flat();
              const uniqueFields = [...new Set(joinedFields)];
              const isCancelable = payloadGroup.some((p) => p.isCancelable === false) ? false : void 0;
              const filter2 = payloadGroup[0].filter;
              const filterParams = new URLSearchParams(filter2);
              const uniqueFilterKeys = [...new Set(filterParams.keys())];
              const filterHasInvalidDuplicates = uniqueFilterKeys.some((key) => {
                const values = filterParams.getAll(key);
                return values.length > 1 && new Set(values).size > 1;
              });
              const type = payloadGroup[0].type;
              if (filterHasInvalidDuplicates) {
                result = of(
                  messageActions.saveMessage({
                    message: localized.duplicateFilterMessage,
                    mode: "error",
                    values: { type, filter: filter2 }
                  })
                );
              }
              if (!filterHasInvalidDuplicates && uniqueFields.length > 0) {
                result = of(
                  this._entityActions.fetchRequest.request(
                    {
                      type,
                      filter: filter2,
                      fields: uniqueFields
                    },
                    { isCancelable }
                  )
                );
              }
            }
            return result;
          })
        )
      )
    );
  }
  buildFetchRequestEpics() {
    const sendRequestEpic = this.buildRequestEpic(this._entityActions.fetchRequest, (payload) => this._serviceFactory(payload.type).get(payload), false);
    const updateViewStatusEpic = (action$) => action$.pipe(
      filter(isActionOf(this._entityActions.fetchRequest.request)),
      map(
        (action) => this._entityActions.fetchStatus({
          requestStatus: "inProgress",
          filter: action.payload.filter,
          entityType: action.payload.type
        })
      )
    );
    return [sendRequestEpic, updateViewStatusEpic];
  }
  buildFetchRequestSuccessEpic() {
    return (action$) => action$.pipe(
      filter(isActionOf(this._entityActions.fetchRequest.success)),
      mergeMap((action) => {
        const { items, total } = action.payload.responsePayload;
        let result;
        if (items?.length) {
          const entityType = action.payload.requestPayload.type;
          const schema2 = this._schemaMapper.getSchema(entityType);
          const normalizedItems = normalize(items, [schema2]);
          const entities = normalizedItems.entities;
          const saveEntityActions = Object.entries(
            entities
          ).map(([type, items2]) => this._entityActions.save({ type, items: items2 }));
          result = merge(
            of(...saveEntityActions),
            of(
              this._viewActions.updateKeys({
                entity: action.payload.requestPayload.type,
                keys: normalizedItems.result,
                filter: action.payload.requestPayload.filter,
                total
              })
            ),
            of(
              this._entityActions.fetchStatus({
                requestStatus: "idle",
                entityType: action.payload.requestPayload.type,
                filter: action.payload.requestPayload.filter
              })
            )
          );
        } else {
          result = merge(
            of(
              this._viewActions.updateKeys({
                entity: action.payload.requestPayload.type,
                keys: [],
                filter: action.payload.requestPayload.filter,
                total
              })
            ),
            of(
              this._entityActions.fetchStatus({
                requestStatus: "idle",
                entityType: action.payload.requestPayload.type,
                filter: action.payload.requestPayload.filter
              })
            )
          );
        }
        return result;
      })
    );
  }
  buildFetchRequestFailureEpic() {
    return (action$) => action$.pipe(
      filter(isActionOf(this._entityActions.fetchRequest.failure)),
      mergeMap((action) => {
        let showMessageAction = messageActions.saveMessage({
          message: localized.entityFetchFailed,
          values: {
            type: action.payload?.requestPayload?.type
          },
          mode: "error"
        });
        const errorToShow = action.payload?.errors?.find((e) => e.severity > 0);
        if (errorToShow) {
          showMessageAction = messageActions.showCodeMessage({
            code: errorToShow.code,
            mode: "error"
          });
        }
        return of(
          this._entityActions.fetchStatus({
            requestStatus: "idle",
            filter: action.payload.requestPayload.filter,
            entityType: action.payload.requestPayload.type
          }),
          showMessageAction
        );
      })
    );
  }
  buildCollectGarbageEpic() {
    const collectGarbageDelay = 12e4;
    return (action$, state$) => action$.pipe(
      //cleaning entity state triggered in 2 mins after first save data to state or after previous garbage collection
      filter(isActionOf([this._entityActions.save, this._entityActions.collectGarbage])),
      bufferTime(collectGarbageDelay),
      filter((actions) => actions.length > 0),
      mergeMap(() => {
        const resultActions = [];
        const state = state$.value;
        const mapper = new EntityKeysMapper(this._schemaMapper, this._entityTypes);
        const referencesToClean = Object.entries(state.entities.references).filter(([_, count]) => count === 0).map((i) => i[0]);
        if (referencesToClean.length > 0) {
          const keysToRemove = Object.entries(
            referencesToClean.map((key) => mapper.mapToObjectKey(key)).reduce((store, { type, id }) => {
              if (!store[type]) {
                store[type] = [];
              }
              if (!store[type].includes(id)) {
                store[type].push(id);
              }
              return store;
            }, {})
          ).map(
            ([type, ids]) => ({
              type,
              ids
            })
          );
          resultActions.push(this._entityActions.cleanReferenceCounter({ references: referencesToClean }));
          resultActions.push(...keysToRemove.map((payload) => this._entityActions.clean(payload)));
        }
        resultActions.push(this._entityActions.collectGarbage());
        return of(...resultActions);
      })
    );
  }
}
export {
  EntityEpicsBuilder,
  localized
};
//# sourceMappingURL=epics.js.map
